<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const subPages = [
  {
    title: 'Pengeluaran',
    desc: 'Catat semua biaya operasional toko: bahan baku, gaji, listrik, sewa, dan lainnya. Dilengkapi upload foto struk dan filter tanggal.',
    icon: 'ri-wallet-3-line',
    color: 'error',
    to: '/userguide/keuangan/pengeluaran',
    waktu: '10 menit',
    tags: ['Wajib dipelajari pertama'],
  },
  {
    title: 'Kategori Pengeluaran',
    desc: 'Buat label pengelompokan sebelum mencatat pengeluaran. Tanpa kategori, Anda tidak bisa menyimpan catatan pengeluaran apapun.',
    icon: 'ri-price-tag-3-line',
    color: 'warning',
    to: '/userguide/keuangan/kategori',
    waktu: '5 menit',
    tags: ['Setup awal'],
  },
]

const urutan = [
  { no: 1, aksi: 'Buka menu Pengeluaran → Kategori', tujuan: 'Buat minimal 3–5 kategori (contoh: Bahan Baku, Gaji, Listrik)' },
  { no: 2, aksi: 'Buka menu Pengeluaran', tujuan: 'Mulai catat pengeluaran operasional harian' },
  { no: 3, aksi: 'Lakukan secara rutin setiap hari', tujuan: 'Jangan tumpuk input — catat di hari yang sama' },
  { no: 4, aksi: 'Cek Laporan Laba Rugi setiap bulan', tujuan: 'Lihat total pengeluaran vs pendapatan dan hitung untung bersih' },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Keuangan</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-4 mb-4">
          <VAvatar color="error" variant="tonal" size="64" rounded="lg">
            <VIcon icon="ri-money-dollar-circle-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">Modul Keuangan — Pengeluaran</h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Pantau dan catat semua biaya operasional toko Anda
            </p>
          </div>
        </div>
      </VCol>
    </VRow>

    <!-- Penjelasan umum -->
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardText class="pa-5">
            <p class="text-body-2 mb-3">
              Modul Keuangan di PintuKasir membantu Anda mencatat semua
              <strong>pengeluaran operasional</strong> toko — mulai dari beli bahan baku,
              bayar gaji karyawan, tagihan listrik, sampai biaya tak terduga seperti servis
              peralatan. Semua data pengeluaran yang Anda masukkan akan otomatis digunakan
              untuk menghitung <strong>keuntungan bersih</strong> di Laporan Laba Rugi.
            </p>
            <p class="text-body-2 mb-0">
              Modul ini terdiri dari dua bagian yang saling berkaitan:
              <strong>Kategori</strong> (label pengelompokan) dan
              <strong>Pengeluaran</strong> (catatan transaksi). Kategori harus dibuat
              terlebih dahulu sebelum bisa mencatat pengeluaran.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Urutan setup yang benar -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-arrow-right-circle-line" class="me-2" color="primary" />
            Urutan Penggunaan yang Benar
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <VTable>
              <thead>
                <tr>
                  <th style="width:50px">Langkah</th>
                  <th>Yang Harus Dilakukan</th>
                  <th>Tujuan</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="step in urutan" :key="step.no">
                  <td>
                    <VAvatar color="primary" variant="tonal" size="28">
                      <span class="text-caption font-weight-bold">{{ step.no }}</span>
                    </VAvatar>
                  </td>
                  <td class="font-weight-medium text-body-2 py-3">{{ step.aksi }}</td>
                  <td class="text-body-2 text-medium-emphasis">{{ step.tujuan }}</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Sub-halaman -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">📖 Panduan Tersedia</h5>
      </VCol>
      <VCol v-for="page in subPages" :key="page.title" cols="12" md="6">
        <RouterLink :to="page.to" class="text-decoration-none">
          <VCard variant="outlined" hover class="h-100">
            <VCardText class="pa-5">
              <div class="d-flex align-center gap-3 mb-3">
                <VAvatar :color="page.color" variant="tonal" size="48" rounded>
                  <VIcon :icon="page.icon" size="26" />
                </VAvatar>
                <div>
                  <p class="font-weight-bold text-body-1 mb-0">{{ page.title }}</p>
                  <div class="d-flex align-center gap-1">
                    <VIcon icon="ri-time-line" size="12" class="text-medium-emphasis" />
                    <span class="text-caption text-medium-emphasis">{{ page.waktu }}</span>
                  </div>
                </div>
              </div>
              <p class="text-body-2 text-medium-emphasis mb-3">{{ page.desc }}</p>
              <VChip
                v-for="tag in page.tags" :key="tag"
                :color="page.color"
                variant="tonal"
                size="x-small"
                label
                class="me-1"
              >
                {{ tag }}
              </VChip>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Link ke laporan -->
    <VRow class="mt-4 mb-2">
      <VCol cols="12">
        <VAlert type="success" variant="tonal" icon="ri-bar-chart-line">
          <p class="font-weight-bold mb-1">📊 Data pengeluaran langsung terhubung ke Laporan</p>
          <p class="text-body-2 mb-2">
            Setelah Anda mencatat pengeluaran, datanya otomatis masuk ke:
          </p>
          <div class="d-flex flex-wrap gap-2">
            <RouterLink to="/userguide/laporan/laba-rugi" class="text-decoration-none">
              <VChip color="success" variant="tonal" size="small" prepend-icon="ri-arrow-right-line">
                Laporan Laba Rugi
              </VChip>
            </RouterLink>
            <RouterLink to="/userguide/laporan/dashboard" class="text-decoration-none">
              <VChip color="success" variant="tonal" size="small" prepend-icon="ri-arrow-right-line">
                Dashboard Laporan
              </VChip>
            </RouterLink>
          </div>
        </VAlert>
      </VCol>
    </VRow>
  </div>
</template>
